import { PitchSlide, pitchSlides } from './slides';
import { createDefaultLayout } from './defaultLayout';

const STORAGE_KEY = 'spotdove-slides-v1';

const cloneDefaults = (): PitchSlide[] => JSON.parse(JSON.stringify(pitchSlides));

const isValidSlide = (slide: unknown): slide is PitchSlide => {
  if (!slide || typeof slide !== 'object') {
    return false;
  }
  const candidate = slide as Partial<PitchSlide>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.title === 'string' &&
    Array.isArray(candidate.highlights) &&
    typeof candidate.imageUrl === 'string' &&
    typeof candidate.tabLabel === 'string'
  );
};

const parseSlides = (raw: string): PitchSlide[] | null => {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(isValidSlide)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

export const loadSlides = (): PitchSlide[] => {
  if (typeof window === 'undefined') {
    return cloneDefaults();
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return cloneDefaults();
  }
  return parseSlides(raw) ?? cloneDefaults();
};

export const saveSlides = (slides: PitchSlide[]): void => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(slides));
};

export const resetSlides = (): PitchSlide[] => {
  window.localStorage.removeItem(STORAGE_KEY);
  return cloneDefaults();
};

export const getDefaultSlide = (slideId: string): PitchSlide | undefined =>
  cloneDefaults().find((slide) => slide.id === slideId);

export const exportSlides = (slides: PitchSlide[]): string => JSON.stringify(slides, null, 2);

export const importSlides = (json: string): PitchSlide[] => {
  const parsed = parseSlides(json);
  if (!parsed) {
    throw new Error('Invalid slides JSON: expected a non-empty array of slides');
  }
  saveSlides(parsed);
  return parsed;
};

const uniqueSlideId = (slides: PitchSlide[]): string => {
  let index = slides.length + 1;
  while (slides.some((slide) => slide.id === `slide-${index}`)) {
    index += 1;
  }
  return `slide-${index}`;
};

/** Blank slide appended in the editor. */
export const createBlankSlide = (existingSlides: PitchSlide[]): PitchSlide => {
  const slideNumber = existingSlides.length + 1;
  return {
    id: uniqueSlideId(existingSlides),
    title: 'New slide',
    highlights: ['Add your first point', 'Add another point', 'Add a third point'],
    imageUrl: 'https://loremflickr.com/800/600/startup,presentation',
    imageAlt: 'Slide photo',
    tabLabel: `SLIDE ${slideNumber}`,
    layout: createDefaultLayout(slideNumber % 2 === 1),
  };
};
